import React, { useEffect, useState } from 'react';
import { Smartphone, X, Share, PlusSquare, Check } from 'lucide-react';

const LS_DISMISSED = 'feron_pwa_hint_dismissed';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isInStandalone = () => window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

/**
 * Shows a one-time hint on the admin page teaching the owner how to install
 * this portal as an app on their iPhone/iPad (Add to Home Screen).
 */
const InstallAppHint = () => {
  const [show, setShow] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);

  useEffect(() => {
    try {
      if (isInStandalone()) return; // already installed
      if (localStorage.getItem(LS_DISMISSED) === '1') return;
      setShow(true);
    } catch {}
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(LS_DISMISSED, '1'); } catch {}
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50 max-w-xs bg-black text-white border border-[#22c55e]/40 rounded-lg shadow-2xl p-4">
        <button onClick={dismiss} aria-label="dismiss" className="absolute top-2 right-2 text-gray-400 hover:text-white"><X size={16} /></button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded bg-[#22c55e] text-black flex items-center justify-center shrink-0">
            <Smartphone size={20} />
          </div>
          <div>
            <div className="font-black text-sm tracking-wide">INSTALL AS APP</div>
            <p className="text-xs text-gray-300 mt-1">Run this owner portal as a full-screen app on your iPhone or iPad.</p>
            <button onClick={() => setFullOpen(true)} className="mt-2 text-xs font-bold text-[#22c55e] hover:underline">
              Show me how →
            </button>
          </div>
        </div>
      </div>

      {fullOpen && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={() => setFullOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setFullOpen(false)} className="absolute top-3 right-3 text-gray-500"><X /></button>
            <h3 className="font-black text-lg">Install on {isIOS() ? 'iPhone / iPad' : 'your device'}</h3>
            <p className="text-xs text-gray-500 mt-1">Runs full-screen, no browser bar, with your Feron logo as the app icon.</p>

            <ol className="mt-5 space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-black text-sm shrink-0">1</div>
                <div className="text-sm">
                  Open this page in <span className="font-bold">Safari</span> on your iPhone/iPad. (Not Chrome — iOS only installs from Safari.)
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-black text-sm shrink-0">2</div>
                <div className="text-sm flex items-center gap-2">
                  Tap the <Share size={18} className="inline text-blue-500" /> <span className="font-bold">Share</span> button at the bottom of Safari.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-black text-sm shrink-0">3</div>
                <div className="text-sm flex items-center gap-2">
                  Scroll and tap <PlusSquare size={18} className="inline" /> <span className="font-bold">Add to Home Screen</span>.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#22c55e] text-black flex items-center justify-center font-black text-sm shrink-0"><Check size={16} /></div>
                <div className="text-sm">
                  Tap <span className="font-bold">Add</span>. The Feron Admin icon will appear on your Home Screen — tap it any time to open the portal like an app.
                </div>
              </li>
            </ol>

            <button onClick={() => { dismiss(); setFullOpen(false); }}
              className="mt-6 w-full h-11 bg-[#22c55e] hover:bg-[#16a34a] text-black font-black tracking-wider rounded">
              GOT IT
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppHint;
